import React, { useEffect, useMemo, useState } from 'react';
import { ReceiptText, CheckCircle2, XCircle, Building2, PieChart, Users } from 'lucide-react';
import ColumnChart from '../../../../components/common/Chart/ColumnChart';
import DonutChart from '../../../../components/common/Chart/DonutChart';
import { getHRExpenses, getEmployees } from '../../hrisApi';

const T = {
  primary: '#1f656c',
  secondary: '#2a8a93',
  highlight: '#4db8c0',
  danger: '#d63342',
  warning: '#f59f00',
  light: 'rgba(31,101,108,0.12)',
};

const now = new Date();
const LAST_YEAR_SAMPLE_MONTHS = Array.from({ length: 13 }, (_, index) =>
  new Date(now.getFullYear(), now.getMonth() - 12 + index, 15).toISOString()
);

const DEFAULT_EXPENSE_CLAIMS = [
  { id: 'c1', expenseType: 'Travel', status: 'Submitted', department: 'Finance', employeeName: 'Ari', totalClaimed: 1860000, expenseDate: LAST_YEAR_SAMPLE_MONTHS[0] },
  { id: 'c2', expenseType: 'Meals', status: 'Approved', department: 'Sales', employeeName: 'Bima', totalClaimed: 930000, expenseDate: LAST_YEAR_SAMPLE_MONTHS[1] },
  { id: 'c3', expenseType: 'Accommodation', status: 'Rejected', department: 'Operations', employeeName: 'Citra', totalClaimed: 2210000, expenseDate: LAST_YEAR_SAMPLE_MONTHS[2] },
  { id: 'c4', expenseType: 'Office Supplies', status: 'Approved', department: 'HR', employeeName: 'Dina', totalClaimed: 420000, expenseDate: LAST_YEAR_SAMPLE_MONTHS[3] },
  { id: 'c5', expenseType: 'Travel', status: 'Submitted', department: 'Sales', employeeName: 'Eka', totalClaimed: 1785000, expenseDate: LAST_YEAR_SAMPLE_MONTHS[4] },
  { id: 'c6', expenseType: 'Meals', status: 'Rejected', department: 'Finance', employeeName: 'Fajar', totalClaimed: 520000, expenseDate: LAST_YEAR_SAMPLE_MONTHS[5] },
  { id: 'c7', expenseType: 'Travel', status: 'Approved', department: 'Sales', employeeName: 'Gita', totalClaimed: 1420000, expenseDate: LAST_YEAR_SAMPLE_MONTHS[6] },
  { id: 'c8', expenseType: 'Office Supplies', status: 'Submitted', department: 'HR', employeeName: 'Haris', totalClaimed: 760000, expenseDate: LAST_YEAR_SAMPLE_MONTHS[7] },
  { id: 'c9', expenseType: 'Accommodation', status: 'Approved', department: 'Operations', employeeName: 'Intan', totalClaimed: 1290000, expenseDate: LAST_YEAR_SAMPLE_MONTHS[8] },
  { id: 'c10', expenseType: 'Meals', status: 'Submitted', department: 'Finance', employeeName: 'Joko', totalClaimed: 350000, expenseDate: LAST_YEAR_SAMPLE_MONTHS[9] },
  { id: 'c11', expenseType: 'Travel', status: 'Rejected', department: 'Sales', employeeName: 'Kiki', totalClaimed: 1120000, expenseDate: LAST_YEAR_SAMPLE_MONTHS[10] },
  { id: 'c12', expenseType: 'Office Supplies', status: 'Approved', department: 'HR', employeeName: 'Lina', totalClaimed: 610000, expenseDate: LAST_YEAR_SAMPLE_MONTHS[11] },
  { id: 'c13', expenseType: 'Meals', status: 'Submitted', department: 'Finance', employeeName: 'Maya', totalClaimed: 980000, expenseDate: LAST_YEAR_SAMPLE_MONTHS[12] },
];

const DEFAULT_EMPLOYEES = [
  { id: 'e1', fullName: 'Ari', status: 'Active', department: 'Finance' },
  { id: 'e2', fullName: 'Bima', status: 'Onboarding', department: 'Sales' },
  { id: 'e3', fullName: 'Citra', status: 'Active', department: 'Operations' },
  { id: 'e4', fullName: 'Dina', status: 'Probation', department: 'HR' },
  { id: 'e5', fullName: 'Eka', status: 'Active', department: 'Sales' },
  { id: 'e6', fullName: 'Fajar', status: 'Resigned', department: 'Finance' },
  { id: 'e7', fullName: 'Gita', status: 'Active', department: 'Operations' },
];

const normalizeRecords = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.content)) return payload.content;
  return [];
};

const isCurrentMonth = (dateValue) => {
  if (!dateValue) return false;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
};

const countBy = (items, key, initialValue = 0, reducer = (count, item) => count + 1) =>
  items.reduce((acc, item) => {
    const label = String(item[key] ?? 'Unknown');
    acc[label] = reducer(acc[label] ?? initialValue, item);
    return acc;
  }, {});

const formatCurrency = (value) => `Rp ${Number(value || 0).toLocaleString('id-ID')}`;

const DATE_RANGE_OPTIONS = [
  { value: 'lastYear', label: 'Last Year' },
  { value: 'lastQuarter', label: 'Last Quarter' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'lastWeek', label: 'Last Week' },
];

const GROUP_BY_OPTIONS = [
  { value: 'yearly', label: 'Yearly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'daily', label: 'Daily' },
];

const parseDateValue = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
const endOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);

const getLastYearRange = (today) => ({
  start: new Date(today.getFullYear() - 1, today.getMonth(), 1),
  end: endOfMonth(today),
});

const getLastQuarterRange = (today) => {
  const month = today.getMonth();
  const currentQuarterStart = Math.floor(month / 3) * 3;
  const lastQuarterEnd = new Date(today.getFullYear(), currentQuarterStart, 0);
  const lastQuarterStart = new Date(lastQuarterEnd.getFullYear(), Math.floor(lastQuarterEnd.getMonth() / 3) * 3, 1);
  return { start: lastQuarterStart, end: endOfDay(lastQuarterEnd) };
};

const getLastMonthRange = (today) => {
  const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return { start, end: endOfMonth(start) };
};

const getLastWeekRange = (today) => ({
  start: startOfDay(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6)),
  end: endOfDay(today),
});

const getDateRange = (option, today) => {
  switch (option) {
    case 'lastYear':
      return getLastYearRange(today);
    case 'lastQuarter':
      return getLastQuarterRange(today);
    case 'lastMonth':
      return getLastMonthRange(today);
    case 'lastWeek':
      return getLastWeekRange(today);
    default:
      return getLastYearRange(today);
  }
};

const getPeriodKey = (date, period) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const weekNumber = (() => {
    const temp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = temp.getUTCDay() || 7;
    temp.setUTCDate(temp.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(temp.getUTCFullYear(), 0, 1));
    return Math.ceil((((temp - yearStart) / 86400000) + 1) / 7);
  })();

  switch (period) {
    case 'yearly':
      return `${year}`;
    case 'quarterly':
      return `${year}-Q${Math.floor((month - 1) / 3) + 1}`;
    case 'monthly':
      return `${year}-${String(month).padStart(2, '0')}`;
    case 'weekly':
      return `${year}-W${String(weekNumber).padStart(2, '0')}`;
    case 'daily':
      return `${year}-${String(month).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    default:
      return `${year}-${String(month).padStart(2, '0')}`;
  }
};

const getPeriodLabel = (date, period) => {
  switch (period) {
    case 'yearly':
      return `${date.getFullYear()}`;
    case 'quarterly':
      return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
    case 'monthly':
      return date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    case 'weekly': {
      const startOfWeek = new Date(date);
      const day = startOfWeek.getDay() || 7;
      startOfWeek.setDate(startOfWeek.getDate() - day + 1);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    case 'daily':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    default:
      return date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
  }
};

const buildPeriodBuckets = (range, period) => {
  const buckets = [];
  if (!range?.start || !range?.end) return buckets;

  const cursor = new Date(range.start);
  if (period === 'yearly') {
    cursor.setMonth(0, 1);
    while (cursor <= range.end) {
      buckets.push({ key: getPeriodKey(cursor, period), label: getPeriodLabel(cursor, period), date: new Date(cursor) });
      cursor.setFullYear(cursor.getFullYear() + 1);
    }
    return buckets;
  }

  if (period === 'quarterly') {
    cursor.setMonth(Math.floor(cursor.getMonth() / 3) * 3, 1);
    while (cursor <= range.end) {
      buckets.push({ key: getPeriodKey(cursor, period), label: getPeriodLabel(cursor, period), date: new Date(cursor) });
      cursor.setMonth(cursor.getMonth() + 3, 1);
    }
    return buckets;
  }

  if (period === 'monthly') {
    cursor.setDate(1);
    while (cursor <= range.end) {
      buckets.push({ key: getPeriodKey(cursor, period), label: getPeriodLabel(cursor, period), date: new Date(cursor) });
      cursor.setMonth(cursor.getMonth() + 1, 1);
    }
    return buckets;
  }

  if (period === 'weekly') {
    const day = cursor.getDay() || 7;
    cursor.setDate(cursor.getDate() - day + 1);
    while (cursor <= range.end) {
      buckets.push({ key: getPeriodKey(cursor, period), label: getPeriodLabel(cursor, period), date: new Date(cursor) });
      cursor.setDate(cursor.getDate() + 7);
    }
    return buckets;
  }

  cursor.setHours(0, 0, 0, 0);
  while (cursor <= range.end) {
    buckets.push({ key: getPeriodKey(cursor, period), label: getPeriodLabel(cursor, period), date: new Date(cursor) });
    cursor.setDate(cursor.getDate() + 1);
  }
  return buckets;
};

const formatMonthYear = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
};

const getDateRangeDescriptor = (range) => {
  if (!range?.start || !range?.end) return 'All time';
  return `${formatMonthYear(range.start)} - ${formatMonthYear(range.end)}`;
};

const Dashboard = () => {
  const [expenseClaims, setExpenseClaims] = useState(DEFAULT_EXPENSE_CLAIMS);
  const [employees, setEmployees] = useState(DEFAULT_EMPLOYEES);
  const [dateRangeOption, setDateRangeOption] = useState('lastYear');
  const [groupByOption, setGroupByOption] = useState('monthly');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([getHRExpenses({ page: 0, size: 200 }), getEmployees({ page: 0, size: 200 })])
      .then(([claimsRes, employeeRes]) => {
        if (!mounted) return;
        const claims = normalizeRecords(claimsRes);
        const workers = normalizeRecords(employeeRes);
        if (claims.length) setExpenseClaims(claims);
        if (workers.length) setEmployees(workers);
      })
      .catch(() => {
        // keep default sample data
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const currentMonthClaims = useMemo(
    () => expenseClaims.filter((claim) => isCurrentMonth(claim.expenseDate || claim.expenseDateTime || claim.date)),
    [expenseClaims]
  );

  const totalClaims = currentMonthClaims.length;
  const approveClaims = currentMonthClaims.filter((claim) => {
    const status = String(claim.status || '').toLowerCase();
    return status === 'submitted' || status === 'pending' || status === 'pending approval' || status === 'need approval' || status === 'approve';
  }).length;
  const rejectedClaims = currentMonthClaims.filter((claim) => String(claim.status || '').toLowerCase().includes('reject')).length;

  const claimsByType = useMemo(() => countBy(currentMonthClaims, 'expenseType'), [currentMonthClaims]);
  const employeeStatusCounts = useMemo(() => countBy(employees, 'status'), [employees]);
  const departmentClaimAmounts = useMemo(
    () => countBy(currentMonthClaims, 'department', 0, (sum, item) => sum + Number(item.totalClaimed || item.amount || 0)),
    [currentMonthClaims]
  );

  const formatMonthYear = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
  };

  const expenseClaimsByMonth = useMemo(() => {
    const dateRange = getDateRange(dateRangeOption, now);
    const buckets = buildPeriodBuckets(dateRange, groupByOption);
    const totals = buckets.reduce((acc, bucket) => {
      acc[bucket.key] = { label: bucket.label, value: 0 };
      return acc;
    }, {});

    for (const claim of expenseClaims) {
      const rawDate = claim.expenseDate || claim.expenseDateTime || claim.date;
      const dateValue = new Date(rawDate);
      if (Number.isNaN(dateValue.getTime())) continue;
      if (dateValue < dateRange.start || dateValue > dateRange.end) continue;

      const key = getPeriodKey(dateValue, groupByOption);
      if (totals[key]) {
        totals[key].value += Number(claim.totalClaimed || claim.amount || 0);
      }
    }

    const sortedKeys = buckets.map((bucket) => bucket.key);
    return {
      labels: sortedKeys.map((key) => totals[key].label),
      series: sortedKeys.map((key) => totals[key].value),
      total: sortedKeys.reduce((sum, key) => sum + totals[key].value, 0),
      descriptor: getDateRangeDescriptor(dateRange),
    };
  }, [expenseClaims, dateRangeOption, groupByOption]);

  const stats = [
    { label: 'Expense Claims (This Month)', value: totalClaims, icon: ReceiptText, color: T.primary, bg: T.light },
    { label: 'Approve Claims (This Month)', value: approveClaims, icon: CheckCircle2, color: T.secondary, bg: 'rgba(42,138,147,0.12)' },
    { label: 'Rejected (This Month)', value: rejectedClaims, icon: XCircle, color: T.danger, bg: 'rgba(214,51,66,0.12)' },
  ];

  const categoryLabels = Object.keys(claimsByType);
  const categorySeries = Object.values(claimsByType);
  const employeeStatuses = Object.keys(employeeStatusCounts);
  const statusSeries = Object.values(employeeStatusCounts);
  const departmentLabels = Object.keys(departmentClaimAmounts);
  const departmentSeries = Object.values(departmentClaimAmounts).map((value) => Math.round(value / 1000));

  return (
    <>
      <div className="page-header mb-4">
        <div className="row align-items-center">
          <div className="col">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center"
                style={{ width: 48, height: 48, backgroundColor: T.primary, flexShrink: 0 }}
              >
                <ReceiptText size={24} color="#fff" />
              </div>
              <div>
                <h2 className="page-title mb-1">Dashboard Expense</h2>
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                  Snapshot of expense claims, approval flow, and employee status by department.
                </p>
              </div>
            </div>
          </div>
          <div className="col-auto">
            <span className="badge bg-success-soft text-success">This month</span>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        {stats.map(({ label, value, icon: Icon, color, bg }) => (
          <div className="col-12 col-md-4" key={label}>
            <div className="card border-0 shadow-sm h-100" style={{ borderTop: `3px solid ${color}` }}>
              <div className="card-body d-flex align-items-center gap-3">
                <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 50, height: 50, background: bg }}>
                  <Icon size={20} color={color} />
                </div>
                <div>
                  <div className="text-muted small">{label}</div>
                  <div className="fw-bold mt-2" style={{ fontSize: '1.8rem', color }}>{loading ? '—' : value}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3">
        <div className="col-12">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div>
                <h3 className="card-title mb-1" style={{ fontSize: '1rem' }}>Expense Claims</h3>
                <div className="text-muted small">Total claims by month and year.</div>
              </div>
              <ReceiptText size={20} className="text-muted" />
            </div>
            <div className="card-body" style={{ minHeight: 370 }}>
              <div className="row g-2 justify-content-end mb-3">
                <div className="col-auto">
                  <label className="form-label small mb-1">Date range</label>
                  <select
                    className="form-select form-select-sm"
                      style={{
                        minWidth: '180px',
                        padding: '7px 12px',
                        borderRadius: '8px'
                      }}
                    value={dateRangeOption}
                    onChange={(e) => setDateRangeOption(e.target.value)}
                  >
                    {DATE_RANGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-auto">
                  <label className="form-label small mb-1">Group by</label>
                  <select
                    className="form-select form-select-sm"
                      style={{
                        minWidth: '180px',
                        padding: '7px 12px',
                        borderRadius: '8px'
                      }}
                    value={groupByOption}
                    onChange={(e) => setGroupByOption(e.target.value)}
                  >
                    {GROUP_BY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                {/* <div className="col-12 col-md-4 text-end">
                  <div className="text-muted small">Date span</div>
                  <div className="fw-bold">{expenseClaimsByMonth.descriptor}</div>
                </div> */}
              </div>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <div className="text-muted small">Total</div>
                  <div className="fw-bold" style={{ fontSize: '1.7rem' }}>{formatCurrency(expenseClaimsByMonth.total)}</div>
                </div>
                <div className="text-muted small text-end">
                  {groupByOption.charAt(0).toUpperCase() + groupByOption.slice(1)}
                </div>
              </div>
              <ColumnChart
                data={expenseClaimsByMonth.series}
                categories={expenseClaimsByMonth.labels}
                yAxisTitle="Claim amount (Rp)"
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div>
                <h3 className="card-title mb-1" style={{ fontSize: '1rem' }}>Claims By Type</h3>
                <div className="text-muted small">Distribution of current-month claims by expense category.</div>
              </div>
              <PieChart size={20} className="text-muted" />
            </div>
            <div className="card-body">
              <DonutChart
                series={categorySeries}
                labels={categoryLabels}
                legendPosition="bottom"
                height="330"
                type="type_distinct"
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div>
                <h3 className="card-title mb-1" style={{ fontSize: '1rem' }}>Employee Advanced Status</h3>
                <div className="text-muted small">Active, onboarding, probation, and separated headcount.</div>
              </div>
              <Users size={20} className="text-muted" />
            </div>
            <div className="card-body" style={{ minHeight: 370 }}>
              <ColumnChart
                data={statusSeries}
                categories={employeeStatuses}
                yAxisTitle="Employees"
                singleColor={T.highlight}
              />
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div>
                <h3 className="card-title mb-1" style={{ fontSize: '1rem' }}>Department wise expense claims</h3>
                <div className="text-muted small">Claim totals split by department.</div>
              </div>
              <Building2 size={20} className="text-muted" />
            </div>
            <div className="card-body" style={{ minHeight: 370 }}>
              <ColumnChart
                data={departmentSeries}
                categories={departmentLabels}
                yAxisTitle="Claim total (Rp thousands)"
                // variant="wrapped"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
